import { useEffect, useState } from 'react';
import { useOfflineSync } from '@/hooks/useOfflineSync';

export function OfflineSyncBanner() {
  const { isOnline, isSyncing, pendingCount, syncNow } = useOfflineSync();
  const [showSynced, setShowSynced] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  // Track when we go offline
  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
      setShowSynced(false);
    }
  }, [isOnline]);

  // Show "All synced" briefly after coming back online and finishing sync
  useEffect(() => {
    if (isOnline && wasOffline && !isSyncing && pendingCount === 0) {
      setShowSynced(true);
      setWasOffline(false);
      const timer = setTimeout(() => setShowSynced(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline, isSyncing, pendingCount]);

  // Nothing to show
  if (isOnline && !isSyncing && pendingCount === 0 && !showSynced) {
    return null;
  }

  // "All synced" flash
  if (showSynced) {
    return (
      <div className="bg-green-500 text-white text-center text-xs font-medium py-1.5 px-3">
        All synced
      </div>
    );
  }

  // Syncing state
  if (isSyncing) {
    return (
      <div className="bg-blue-500 text-white text-center text-xs font-medium py-1.5 px-3 flex items-center justify-center gap-2">
        <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Syncing...
      </div>
    );
  }

  // Offline with pending actions
  if (!isOnline) {
    return (
      <div className="bg-yellow-500 text-white text-center text-xs font-medium py-1.5 px-3">
        Offline{pendingCount > 0 ? ` \u2014 ${pendingCount} action${pendingCount === 1 ? '' : 's'} pending` : ''}
      </div>
    );
  }

  // Online but has pending items (sync hasn't started yet or items from errors)
  if (pendingCount > 0) {
    return (
      <button
        onClick={syncNow}
        className="w-full bg-yellow-500 text-white text-center text-xs font-medium py-1.5 px-3 hover:bg-yellow-600 transition-colors"
      >
        {pendingCount} action{pendingCount === 1 ? '' : 's'} pending — tap to sync
      </button>
    );
  }

  return null;
}
